<script lang="ts">
  /**
   * Individual toast notification component
   * Auto-dismisses after duration or can be manually closed
   */

  import { fade, fly } from 'svelte/transition';
  import type { ToastType } from '$lib/stores/toast';

  export let id: string;
  export let type: ToastType;
  export let message: string;
  export let onDismiss: (id: string) => void;

  // Icon for each type
  const icons = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠'
  };

  // Handle dismiss
  function dismiss() {
    onDismiss(id);
  }
</script>

<div
  class="toast toast-{type}"
  role="alert"
  aria-live="polite"
  transition:fly={{ y: -20, duration: 300 }}
>
  <div class="toast-icon">
    {icons[type]}
  </div>
  <div class="toast-message">
    {message}
  </div>
  <button class="toast-close" on:click={dismiss} aria-label="Dismiss notification">
    ×
  </button>
</div>

<style>
  .toast {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 300px;
    max-width: 500px;
    padding: 1rem 1.25rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-md, 16px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    color: var(--text);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .toast-success {
    border-left: 4px solid #22c55e;
  }

  .toast-error {
    border-left: 4px solid #ef4444;
  }

  .toast-info {
    border-left: 4px solid #3b82f6;
  }

  .toast-warning {
    border-left: 4px solid #f59e0b;
  }

  .toast-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: bold;
  }

  .toast-success .toast-icon {
    color: #22c55e;
  }

  .toast-error .toast-icon {
    color: #ef4444;
  }

  .toast-info .toast-icon {
    color: #3b82f6;
  }

  .toast-warning .toast-icon {
    color: #f59e0b;
  }

  .toast-message {
    flex: 1;
  }

  .toast-close {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--text-muted);
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.2s ease;
  }

  .toast-close:hover {
    background: var(--chip-hover);
    color: var(--text);
  }

  .toast-close:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    .toast {
      min-width: unset;
      max-width: calc(100vw - 2rem);
    }
  }
</style>
